import type {
  FeedbackSentiment, Suggestion, Template, GalleryItem, SelectOption,
  Attachment, FilterOption,
} from '../types/index.js';

// === Event detail interfaces ===

export interface LoquixSubmitDetail {
  content: string;
  attachments?: File[];
}

export interface LoquixStopDetail {
  messageId: string;
}

export interface LoquixRegenerateDetail {
  messageId: string;
}

export interface LoquixFeedbackDetail {
  messageId: string;
  sentiment: FeedbackSentiment;
  comment?: string;
}

export interface LoquixBranchDetail {
  fromMessageId: string;
}

export interface LoquixCopyDetail {
  messageId: string;
  content: string;
}

export interface LoquixModeChangeDetail {
  from: string;
  to: string;
}

export interface LoquixModelChangeDetail {
  from: string;
  to: string;
}

export interface LoquixChangeDetail {
  value: string;
}

export interface LoquixPauseDetail {
  messageId?: string;
}

export interface LoquixResumeDetail {
  messageId?: string;
}

export interface LoquixEditDetail {
  messageId: string;
  content: string;
}

export interface LoquixEditStartDetail {
  messageId: string;
  content: string;
}

export interface LoquixEditSubmitDetail {
  messageId: string;
  oldContent: string;
  newContent: string;
}

export interface LoquixEditCancelDetail {
  messageId: string;
}

// === Wayfinder events (Phase 2) ===

export interface LoquixSuggestionSelectDetail {
  suggestion: Suggestion;
}

export interface LoquixTemplateSelectDetail {
  template: Template;
}

export interface LoquixTemplatePickerOpenDetail {}

export interface LoquixTemplatePickerCloseDetail {}

export interface LoquixGallerySelectDetail {
  item: GalleryItem;
}

export interface LoquixNudgeDismissDetail {
  nudgeId: string;
}

export interface LoquixNudgeActionDetail {
  nudgeId: string;
  action: string;
}

export interface LoquixSelectChangeDetail {
  value: string;
  option: SelectOption;
}

// === Tuner events (Phase 3) ===

export interface LoquixAttachmentAddDetail {
  attachments: Attachment[];
}

export interface LoquixAttachmentRemoveDetail {
  attachment: Attachment;
}

export interface LoquixAttachmentRetryDetail {
  attachment: Attachment;
}

export interface LoquixParameterChangeDetail {
  id: string;
  value: unknown;
  values: Record<string, unknown>;
}

export interface LoquixParameterCommitDetail {
  id: string;
  value: unknown;
  values: Record<string, unknown>;
}

export interface LoquixPresetChangeDetail {
  preset: string;
}

export interface LoquixFilterChangeDetail {
  values: string[];
  negativePrompt?: string;
}

// === Helper to create typed CustomEvents ===

export function createLoquixEvent<T>(
  name: string,
  detail: T,
  options?: Partial<CustomEventInit<T>>,
): CustomEvent<T> {
  return new CustomEvent<T>(name, {
    bubbles: true,
    composed: true,
    detail,
    ...options,
  });
}

// === Global event map augmentation ===

declare global {
  interface HTMLElementEventMap {
    'loquix-submit': CustomEvent<LoquixSubmitDetail>;
    'loquix-stop': CustomEvent<LoquixStopDetail>;
    'loquix-regenerate': CustomEvent<LoquixRegenerateDetail>;
    'loquix-feedback': CustomEvent<LoquixFeedbackDetail>;
    'loquix-branch': CustomEvent<LoquixBranchDetail>;
    'loquix-copy': CustomEvent<LoquixCopyDetail>;
    'loquix-mode-change': CustomEvent<LoquixModeChangeDetail>;
    'loquix-model-change': CustomEvent<LoquixModelChangeDetail>;
    'loquix-change': CustomEvent<LoquixChangeDetail>;
    'loquix-pause': CustomEvent<LoquixPauseDetail>;
    'loquix-resume': CustomEvent<LoquixResumeDetail>;
    'loquix-edit': CustomEvent<LoquixEditDetail>;
    'loquix-edit-start': CustomEvent<LoquixEditStartDetail>;
    'loquix-edit-submit': CustomEvent<LoquixEditSubmitDetail>;
    'loquix-edit-cancel': CustomEvent<LoquixEditCancelDetail>;
    'loquix-suggestion-select': CustomEvent<LoquixSuggestionSelectDetail>;
    'loquix-template-select': CustomEvent<LoquixTemplateSelectDetail>;
    'loquix-template-picker-open': CustomEvent<LoquixTemplatePickerOpenDetail>;
    'loquix-template-picker-close': CustomEvent<LoquixTemplatePickerCloseDetail>;
    'loquix-gallery-select': CustomEvent<LoquixGallerySelectDetail>;
    'loquix-nudge-dismiss': CustomEvent<LoquixNudgeDismissDetail>;
    'loquix-nudge-action': CustomEvent<LoquixNudgeActionDetail>;
    'loquix-select-change': CustomEvent<LoquixSelectChangeDetail>;
    'loquix-attachment-add': CustomEvent<LoquixAttachmentAddDetail>;
    'loquix-attachment-remove': CustomEvent<LoquixAttachmentRemoveDetail>;
    'loquix-attachment-retry': CustomEvent<LoquixAttachmentRetryDetail>;
    'loquix-parameter-change': CustomEvent<LoquixParameterChangeDetail>;
    'loquix-parameter-commit': CustomEvent<LoquixParameterCommitDetail>;
    'loquix-preset-change': CustomEvent<LoquixPresetChangeDetail>;
    'loquix-filter-change': CustomEvent<LoquixFilterChangeDetail>;
  }
}
